import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { api } from '../../services/api';
import ListsTab from './projectAction/ListsTab';
import MyCallsTab from './projectAction/MyCallsTab';
import ProgressTab from './projectAction/ProgressTab';
import ProjectBoardTab from './projectAction/ProjectBoardTab';
import { ProjectOption } from './projectAction/shared';

type TabValue = 'my-calls' | 'projects' | 'lists' | 'progress';

interface ProjectActionDialogProps {
  open: boolean;
  onClose: () => void;
  /** Full ADMIN — authoring a list means authoring the SQL behind it. */
  isAdmin?: boolean;
  /**
   * Leads at least one project: Head of Project or Deputy Head there, or an IC
   * on a project that has neither.
   */
  leadsProjects?: boolean;
}

/**
 * Project Action: getting a cohort of agents called.
 *
 * Management writes a query and puts its agents on a project. That project's
 * lead shares them across the core team with a brief. Each team member works
 * their own list — WhatsApp, a tick, a comment back — and management watches
 * the whole thing move.
 *
 * Each role gets only the tabs it can act on, so most people see just "My Calls".
 */
const ProjectActionDialog: React.FC<ProjectActionDialogProps> = ({
  open,
  onClose,
  isAdmin = false,
  leadsProjects = false,
}) => {
  // Project Lists leads, because a lead opening this has work to hand out before
  // they have calls of their own. Everyone else only gets My Calls anyway.
  const tabs = useMemo(() => {
    const out: Array<{ value: TabValue; label: string }> = [];
    if (isAdmin || leadsProjects) out.push({ value: 'projects', label: 'Project Lists' });
    out.push({ value: 'my-calls', label: 'My Calls' });
    if (isAdmin) {
      out.push({ value: 'lists', label: 'Manage Lists' });
      out.push({ value: 'progress', label: 'Progress' });
    }
    return out;
  }, [isAdmin, leadsProjects]);

  const [tab, setTab] = useState<TabValue>('my-calls');
  // Remounts the active tab's subtree, which is how each one re-reads itself —
  // every tab loads on mount, so bumping the key is the refresh.
  const [reloadKey, setReloadKey] = useState(0);
  const [projects, setProjects] = useState<ProjectOption[]>([]);

  // Open on the first tab the user actually has, so the landing tab follows the
  // order above rather than being pinned to one value.
  useEffect(() => {
    if (!open) return;
    setTab(tabs[0]?.value ?? 'my-calls');
    setReloadKey((k) => k + 1);
  }, [open, tabs]);

  // The admin list builder needs a project to bind a list to. Project
  // Verification's admin feed is already exactly that set — every active,
  // frontend-visible project — so it is reused rather than adding an endpoint.
  useEffect(() => {
    if (!open || !isAdmin) return;
    let cancelled = false;
    api
      .getAllProjectVerifications()
      .then((response) => {
        if (cancelled) return;
        setProjects(
          response.projects.map((p) => ({ projectId: p.projectId, projectName: p.projectName }))
        );
      })
      .catch(() => {
        if (!cancelled) setProjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isAdmin]);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
          minHeight: '75vh',
        },
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgb(var(--color-text-rgb) / 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography variant="h6">Project Actions</Typography>
            <Typography variant="body2" sx={{ color: 'rgb(var(--color-text-rgb) / 0.72)' }}>
              Reach the agents who matter to a project — assigned, called and tracked.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton onClick={refresh} size="small" title="Refresh">
              <RefreshIcon />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        {tabs.length > 1 && (
          <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
            {tabs.map((t) => (
              <Tab key={t.value} value={t.value} label={t.label} />
            ))}
          </Tabs>
        )}

        {tab === 'my-calls' && <MyCallsTab key={`my-calls-${reloadKey}`} />}
        {tab === 'projects' && (isAdmin || leadsProjects) && (
          <ProjectBoardTab key={`projects-${reloadKey}`} />
        )}
        {tab === 'lists' && isAdmin && (
          <ListsTab key={`lists-${reloadKey}`} projects={projects} onChanged={refresh} />
        )}
        {tab === 'progress' && isAdmin && <ProgressTab key={`progress-${reloadKey}`} />}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid rgb(var(--color-text-rgb) / 0.08)' }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectActionDialog;
